/**
 * Clock times the way a person says them.
 *
 * Times are stored as 24-hour "HH:MM" strings, but nobody picking a start time
 * for a social thinks in those terms — they think "nine, PM". These helpers
 * take a stored time apart into the three pieces a picker asks for (hour,
 * minute, AM/PM) and put it back together again, so a form can offer twelve
 * hours and four minutes instead of one list of ninety-six.
 */

import { timeToMinutes } from "./dateUtils";

export type Meridiem = "AM" | "PM";

/** Minutes between adjacent choices — quarter hours. */
export const TIME_STEP_MINUTES = 15;

/** The minute choices offered for any hour: :00, :15, :30, :45. */
export const MINUTE_STEPS = Array.from(
  { length: 60 / TIME_STEP_MINUTES },
  (_, i) => i * TIME_STEP_MINUTES,
);

/** 12, 1, 2 … 11 — the hours as a clock face lists them. */
export const HOUR_CHOICES = [12, ...Array.from({ length: 11 }, (_, i) => i + 1)];

export interface TimeParts {
  hour12: number;
  minute: number;
  meridiem: Meridiem;
}

/** The three spoken pieces of a stored "HH:MM", or null if there is no time. */
export function parseTimeParts(time24: string): TimeParts | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time24.trim());
  if (!match) return null;
  const hour = parseInt(match[1] as string, 10);
  const minute = parseInt(match[2] as string, 10);
  if (hour > 23 || minute > 59) return null;
  return {
    hour12: hour % 12 === 0 ? 12 : hour % 12,
    minute,
    meridiem: hour < 12 ? "AM" : "PM",
  };
}

/** The three pieces back into a stored "HH:MM". */
export function toTime24({ hour12, minute, meridiem }: TimeParts): string {
  const base = hour12 % 12;
  const hour = meridiem === "PM" ? base + 12 : base;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** Which half of the day a stored time falls in, or null if there is no time. */
export function meridiemOf(time24: string): Meridiem | null {
  return parseTimeParts(time24)?.meridiem ?? null;
}

/** The tag for a night that runs past midnight. */
export const LATE_NIGHT_TAG = "late-night";

/**
 * Does this event run past midnight?
 *
 * An end time in the morning that lands before the start is the next day's
 * morning — a 9 PM social ending at 1 AM. An event that both starts and ends
 * in the small hours never crosses midnight on this listing, so it does not
 * count, and neither does an end time nobody filled in.
 */
export function endsAfterMidnight(startTime: string, endTime: string): boolean {
  if (!startTime || !endTime) return false;
  if (meridiemOf(endTime) !== "AM") return false;
  return timeToMinutes(endTime) < timeToMinutes(startTime);
}
